import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import Stepper, { PHASES } from "@/components/Stepper";
import { ProjectProvider, useProject } from "@/context/ProjectContext";
import PublishExport from "@/components/PublishExport";
import DocumentModal from "@/components/DocumentModal";
import { buildDocModel } from "@/lib/docModel";
import Phase1 from "@/components/phases/Phase1";
import Phase2 from "@/components/phases/Phase2";
import Phase3 from "@/components/phases/Phase3";
import Phase4 from "@/components/phases/Phase4";
import Phase5 from "@/components/phases/Phase5";
import Phase6 from "@/components/phases/Phase6";
import { ArrowLeft, ArrowRight, Save, CheckCircle2, Loader2, Check, Workflow, FileOutput, Printer } from "lucide-react";

const PHASE_COMPONENTS = { 1: Phase1, 2: Phase2, 3: Phase3, 4: Phase4, 5: Phase5, 6: Phase6 };
const REPORT_FOR_PHASE = { 1: "analysis", 2: "analysis", 3: "proposal", 4: "implementation", 5: "implementation", 6: "roi" };

function SaveIndicator() {
  const { saving, lastSavedAt } = useProject();
  if (saving) {
    return (
      <span data-testid="autosave-indicator" className="text-xs text-slate-400 flex items-center gap-1">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (lastSavedAt) {
    return (
      <span data-testid="autosave-indicator" className="text-xs text-emerald-600 flex items-center gap-1">
        <Check className="h-3.5 w-3.5" /> All changes saved
      </span>
    );
  }
  return null;
}

function WorkspaceInner() {
  const navigate = useNavigate();
  const { project, phaseData, loading, error, updateMeta, saveNow } = useProject();
  const [current, setCurrent] = useState(1);
  const [view, setView] = useState("workflow");
  const [reportType, setReportType] = useState(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (project && !initialized.current) {
      setCurrent(project.current_phase || 1);
      initialized.current = true;
    }
  }, [project]);

  useEffect(() => {
    if (error) {
      toast.error("Project not found");
      navigate("/projects");
    }
  }, [error, navigate]);

  if (loading || !phaseData) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  const goToPhase = (n) => {
    setCurrent(n);
    updateMeta({ current_phase: n });
  };
  const handleNext = () => goToPhase(Math.min(6, current + 1));
  const handleComplete = async () => {
    updateMeta({ status: "completed", current_phase: 6 });
    const ok = await saveNow();
    if (ok) toast.success("Project marked complete");
  };

  const PhaseComp = PHASE_COMPONENTS[current];

  return (
    <Layout>
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200 bg-white/70 backdrop-blur-xl z-40 sticky top-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-to-projects" onClick={() => navigate("/projects")} className="text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="font-display text-lg font-bold text-slate-900 truncate">{project?.name}</h1>
            <p className="text-xs text-slate-400">{PHASES[current - 1].title}</p>
          </div>
          {project?.status === "completed" && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Completed</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center bg-slate-100 rounded-md p-0.5">
            <button
              data-testid="view-workflow-btn"
              onClick={() => setView("workflow")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${view === "workflow" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Workflow className="h-4 w-4" /> Workflow
            </button>
            <button
              data-testid="view-publish-btn"
              onClick={() => setView("publish")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${view === "publish" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <FileOutput className="h-4 w-4" /> Publish & Export
            </button>
          </div>
          <SaveIndicator />
          {view === "workflow" && (
            <button data-testid="print-report-btn" onClick={() => setReportType(REPORT_FOR_PHASE[current])} className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-3 py-2 rounded-md font-medium text-sm flex items-center gap-2" title="Download / print this phase's report">
              <Printer className="h-4 w-4" /> Report
            </button>
          )}
          <button data-testid="save-btn" onClick={() => saveNow().then((ok) => ok && toast.success("Progress saved"))} className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </header>

      {view === "publish" ? (
        <PublishExport />
      ) : (
        <>
      <Stepper current={current} onSelect={goToPhase} />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">Phase {current}</p>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">{PHASES[current - 1].title}</h2>
        </div>

        <div key={current} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <PhaseComp />
        </div>

        <div className="flex items-center justify-between mt-8 max-w-5xl">
          <button data-testid="prev-phase-btn" onClick={() => goToPhase(Math.max(1, current - 1))} disabled={current === 1} className="text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2 disabled:opacity-40">
            <ArrowLeft className="h-4 w-4" /> Previous
          </button>
          {current < 6 ? (
            <button data-testid="next-phase-btn" onClick={handleNext} className="bg-emerald-500 text-white hover:bg-emerald-600 px-5 py-2 rounded-md font-medium text-sm flex items-center gap-2">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="complete-project-btn" onClick={handleComplete} className="bg-emerald-500 text-white hover:bg-emerald-600 px-5 py-2 rounded-md font-medium text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Mark Complete
            </button>
          )}
        </div>
      </div>
        </>
      )}

      {reportType && (
        <DocumentModal
          model={buildDocModel(reportType, project, phaseData)}
          docType={reportType}
          projectId={project.id}
          brandColor={project?.branding?.brand_color || "#10B981"}
          agencyName={project?.branding?.agency_name || "Performance Flow Pro"}
          review={(project?.reviews || {})[reportType]}
          onPublished={() => {}}
          onClose={() => setReportType(null)}
        />
      )}
    </Layout>
  );
}

export default function ProjectWorkspace() {
  const { id } = useParams();
  return (
    <ProjectProvider projectId={id}>
      <WorkspaceInner />
    </ProjectProvider>
  );
}
